<!--Copyright (C) nexleader - All Rights Reserved-->
<!--Unauthorized copying of this file, via any medium is strictly prohibited-->
<!--Proprietary and confidential-->
<!--Written for nexleader <myipsat.com>, 2016-2026-->

<!--
  About the IPSAT — rebuilt 2026-08-26 from Steve's "Dashboard About"
  reference art. Faith-based and values-based differ in four places:
  the fourth dimension (Spiritual Gifts / Personal Values), the closing
  sentence of the Introduction, the whole "Character Is the Foundation"
  block, and the last line of "Your Archetype". Everything else is shared.

  surveyTypeIdentifier arrives from the component (group lite lookup) and
  is '' until that resolves, so each branch tests explicitly for its own
  value rather than using an else — an unresolved group must render
  neither variant instead of defaulting to faith.
-->

<div id="nexleader-wrapper">
  <div class="content animate-panel nexleader-about">

    <!-- ---------- Hero ---------- -->
    <header class="nexleader-about-hero">
      <div class="nexleader-about-hero-copy">
        <div class="nexleader-about-wordmark">
          <img src="/assets/images/logo.svg" width="52" height="42" alt="" />
          <span class="nexleader-about-wordmark-text">IPSAT 2.0</span>
        </div>
        <p class="nexleader-about-eyebrow">A Self-Leadership Operating System</p>

        <h1 class="nexleader-about-headline">
          Discover Your Identity.
          <span class="nexleader-about-headline-accent">
            Reveal Your <span class="nexleader-about-underline">Archetype.</span>
          </span>
        </h1>
        <p class="nexleader-about-lede">
          Gain a practical framework for understanding how you naturally
          think, lead, serve, and contribute&mdash;and how to put that
          understanding to work.
        </p>
      </div>

      <!-- The six archetypes, on the wheel. Decorative here: every
           archetype is named in full on the report itself. -->
      <div class="nexleader-about-ring" aria-hidden="true">
        <span class="nexleader-about-ring-halo"></span>
        <span class="nexleader-about-ring-track"></span>
        <span class="nexleader-about-ring-spoke" style="--angle: 0deg">
          <app-archetype-icon archetype="curious_explorer" size="62"></app-archetype-icon>
        </span>
        <span class="nexleader-about-ring-spoke" style="--angle: 60deg">
          <app-archetype-icon archetype="visionary_strategist" size="62"></app-archetype-icon>
        </span>
        <span class="nexleader-about-ring-spoke" style="--angle: 120deg">
          <app-archetype-icon archetype="encouraging_connector" size="62"></app-archetype-icon>
        </span>
        <span class="nexleader-about-ring-spoke" style="--angle: 180deg">
          <app-archetype-icon archetype="steady_builder" size="62"></app-archetype-icon>
        </span>
        <span class="nexleader-about-ring-spoke" style="--angle: 240deg">
          <app-archetype-icon archetype="reflective_guide" size="62"></app-archetype-icon>
        </span>
        <span class="nexleader-about-ring-spoke" style="--angle: 300deg">
          <app-archetype-icon archetype="bold_initiator" size="62"></app-archetype-icon>
        </span>
      </div>
    </header>

    <!-- ---------- Introduction + the five dimensions ---------- -->
    <section class="hpanel">
      <div class="panel-body">
        <div class="nexleader-about-split">
          <div class="nexleader-about-split-main">
            <h2 class="nexleader-about-section-title">Introduction</h2>
            <p>
              The Identity Profile Self-Awareness Tool (IPSAT) is designed to
              help you better understand and engage the five dimensions of
              your identity.
            </p>
            <p>
              While each dimension provides valuable insight, none tells the
              whole story by itself.
              <span class="nexleader-about-emphasis">
                IPSAT 2.0 integrates them into a unified Identity Profile</span>,
              providing a more complete understanding of
              <span *ngIf="surveyTypeIdentifier === 'faith_based'">
                how God has uniquely designed you to think, lead, serve, and
                contribute.
              </span>
              <span *ngIf="surveyTypeIdentifier === 'values_based'">
                your natural pattern for thinking, leading, serving, and
                contributing.
              </span>
            </p>
          </div>

          <div class="nexleader-about-split-aside">
            <h2 class="nexleader-about-section-title nexleader-about-section-title-green">
              The Five Dimensions
            </h2>
            <ul class="nexleader-about-dimensions">
              <li>Personality</li>
              <li>Strengths</li>
              <li>Skills</li>
              <li>
                <span *ngIf="surveyTypeIdentifier === 'faith_based'">Spiritual Gifts</span>
                <span *ngIf="surveyTypeIdentifier === 'values_based'">Personal Values</span>
              </li>
              <li>Passions</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- ---------- Character is the foundation ---------- -->
    <section class="hpanel">
      <div class="panel-body">
        <div class="nexleader-about-character">
          <div class="nexleader-about-character-copy">
            <h2 class="nexleader-about-section-title">Character Is the Foundation</h2>

            <div *ngIf="surveyTypeIdentifier === 'faith_based'">
              <p>
                God has entrusted you with unique gifts, abilities, and
                opportunities. But lasting influence is built on character.
              </p>
              <p>
                Your Identity Profile helps you understand how God has designed
                you. As you grow in wisdom, humility, and obedience, you become
                a healthier and more effective expression of that design in
                service to God and others.
              </p>
            </div>

            <div *ngIf="surveyTypeIdentifier === 'values_based'">
              <p>
                You are entrusted with unique gifts, abilities, and
                opportunities. But lasting influence is built on character.
              </p>
              <p>
                Your Identity Profile helps you understand how you are wired.
                As you grow in self-awareness, integrity, and responsibility,
                you become a healthier and more effective expression of that
                design in service to others.
              </p>
            </div>
          </div>

          <div class="nexleader-about-character-mark" aria-hidden="true">
            <i class="fa fa-leaf"></i>
          </div>
        </div>
      </div>
    </section>

    <!-- ---------- Four stages ---------- -->
    <section class="hpanel">
      <div class="panel-body">
        <h2 class="nexleader-about-section-title text-center">
          Four Stages of Identity Development
        </h2>

        <ol class="nexleader-about-stages">
          <li class="nexleader-about-stage">
            <span class="nexleader-about-stage-mark" aria-hidden="true">
              <i class="fa fa-search"></i>
            </span>
            <span class="nexleader-about-stage-index" aria-hidden="true">1</span>
            <h3 class="nexleader-about-stage-name">Exploration Stage</h3>
            <p>
              You are discovering the five dimensions of your Identity Profile
              but have not yet integrated them intentionally.
            </p>
          </li>

          <li class="nexleader-about-stage">
            <span class="nexleader-about-stage-mark" aria-hidden="true">
              <i class="fa fa-puzzle-piece"></i>
            </span>
            <span class="nexleader-about-stage-index" aria-hidden="true">2</span>
            <h3 class="nexleader-about-stage-name">Identification Stage</h3>
            <p>
              You recognize the patterns within your Identity Profile and
              understand how the five dimensions work together.
            </p>
          </li>

          <li class="nexleader-about-stage">
            <span class="nexleader-about-stage-mark" aria-hidden="true">
              <i class="fa fa-bullseye"></i>
            </span>
            <span class="nexleader-about-stage-index" aria-hidden="true">3</span>
            <h3 class="nexleader-about-stage-name">Application Stage</h3>
            <p>
              You intentionally leverage your Identity Profile with increasing
              confidence, consistency, and effectiveness.
            </p>
          </li>

          <li class="nexleader-about-stage">
            <span class="nexleader-about-stage-mark" aria-hidden="true">
              <i class="fa fa-line-chart"></i>
            </span>
            <span class="nexleader-about-stage-index" aria-hidden="true">4</span>
            <h3 class="nexleader-about-stage-name">Maturation Stage</h3>
            <p>
              Through experience, reflection, and continued growth, you steward
              your Identity Profile with greater wisdom and impact.
            </p>
          </li>
        </ol>
      </div>
    </section>

    <!-- ---------- Score + archetype ---------- -->
    <div class="row">
      <div class="col-md-5">
        <section class="hpanel">
          <div class="panel-body">
            <h2 class="nexleader-about-section-title nexleader-about-section-title-green">
              Identity Profile Score
            </h2>
            <div class="nexleader-about-score">
              <div class="nexleader-about-score-ring" aria-hidden="true">
                <span class="nexleader-about-score-ring-inner">
                  <span class="nexleader-about-score-value">100</span>
                  <span class="nexleader-about-score-unit">MAX</span>
                </span>
              </div>
              <div class="nexleader-about-score-copy">
                <p>The maximum Identity Profile Score is 100.</p>
                <p>
                  There are five categories within your Identity Profile, each
                  with a maximum score of 20.
                </p>
                <p>
                  Your score reflects your current level of self-awareness and
                  intentional engagement with each dimension of your Identity
                  Profile.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="col-md-7">
        <section class="hpanel nexleader-about-archetype">
          <div class="panel-body">
            <h2 class="nexleader-about-section-title nexleader-about-section-title-green">
              Your Archetype
            </h2>
            <p>Your archetype is what makes IPSAT 2.0 unique.</p>
            <p>
              Your archetype is not simply another assessment result. It
              represents the integrated pattern revealed by your Identity
              Profile.
            </p>
            <p>
              IPSAT combines quantitative assessment data with qualitative
              insights from your self-authored identity statements to identify
              the archetype that best fits your integrated identity.
            </p>
            <p>
              Your archetype provides a
              <span class="nexleader-about-emphasis">practical language</span>
              for understanding, communicating, and intentionally applying your
              Identity Profile, making it easier to live and lead from
              <span *ngIf="surveyTypeIdentifier === 'faith_based'">
                the identity God has entrusted to you.
              </span>
              <span *ngIf="surveyTypeIdentifier === 'values_based'">
                the identity that is uniquely yours.
              </span>
            </p>
          </div>
        </section>
      </div>
    </div>

    <p class="nexleader-about-footnote">
      IPSAT 2.0 is a self-awareness tool, not a diagnostic. Results are most
      valuable when combined with reflection and feedback from others.
    </p>

  </div>
</div>
